import {
  Bolt,
  Brain,
  Chart,
  Chat,
  Chev,
  Doc,
  Globe,
  Lock,
  Logout,
  Notebook,
  Question,
  Settings as SettingsIcon,
  Spark,
  User,
  Users,
} from "@scoach/ui/icons";
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { ThemeToggle } from "../../../components/ThemeToggle.tsx";
import { isAdminEmail } from "../../../lib/admins.ts";
import { signOut } from "../../../lib/firebase.ts";
import { useAuthStore } from "../../auth/store.ts";
import { NotificationBell } from "../../notifications/NotificationBell.tsx";
import { OPEN_ONBOARDING_AGENT_EVENT } from "../../onboarding-agent/OnboardingAgentWidget.tsx";
import { OnboardingVideoModal } from "../../onboarding/OnboardingVideoModal.tsx";

export interface DashHeaderProps {
  /** Custom handler for starting a meeting / action. If not provided, defaults to triggering quick meeting on Dashboard. */
  onStartNew?: () => void;
  /** Custom button label. Defaults to "Quick Meeting". */
  startLabel?: string;
}

export function DashHeader(_props: DashHeaderProps) {
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const bubbleHidden = useAuthStore((s) => s.onboardingBubbleHidden);
  const setBubbleHidden = useAuthStore((s) => s.setOnboardingBubbleHidden);
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem("sellsynq.sidebarCollapsed") === "true";
    } catch {
      return false;
    }
  });
  const ref = useRef<HTMLDivElement>(null);

  function toggleCollapse() {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("sellsynq.sidebarCollapsed", String(next));
      } catch {}
      return next;
    });
  }

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const displayName = useAuthStore((s) => s.displayName);
  const storeEmail = useAuthStore((s) => s.email);
  const fallbackName = displayName ?? storeEmail?.split("@")[0] ?? "";
  const initials =
    user?.initials ??
    (fallbackName
      .split(" ")
      .map((w) => w[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) ||
      "?");
  const color = user?.color ?? "#6366F1";
  const name = user?.name ?? fallbackName;
  const email = user?.email ?? storeEmail ?? "";

  async function handleSignOut() {
    setOpen(false);
    try {
      await signOut();
    } catch {
      /* dev mode */
    }
    clear();
    nav({ to: "/signin" });
  }

  function handleStartMeeting() {
    if (_props.onStartNew) {
      _props.onStartNew();
      return;
    }
    try {
      sessionStorage.setItem("sellsynq:auto-quick-live", "1");
    } catch {}
    window.dispatchEvent(new Event("sellsynq:open-quick-meeting"));
    nav({ to: "/dashboard" });
  }

  return (
    <aside className={`app-sidebar ${collapsed ? "collapsed" : ""}`}>
      {/* Brand Header & Toggle */}
      <div className="sidebar-brand-wrapper">
        <Link to="/dashboard" className="sidebar-brand" title="SellSynq - Your Invisible AI Sales Assistant">
          <div className="sidebar-brand-logo">
            <img src="/supercloud-mark.svg" alt="SellSynq" width={22} height={22} />
          </div>
          {!collapsed && (
            <div className="sidebar-brand-text">
              <div className="sidebar-brand-name">SellSynq</div>
              <div className="sidebar-brand-sub" title="Your Invisible AI Sales Assistant">
                Your Invisible AI Sales Assistant
              </div>
            </div>
          )}
        </Link>
        <button
          type="button"
          className="sidebar-toggle-btn"
          onClick={toggleCollapse}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <span
            style={{
              display: "inline-flex",
              transform: collapsed ? "rotate(0deg)" : "rotate(180deg)",
              transition: "transform 0.2s cubic-bezier(0.16, 1, 0.3, 1)",
            }}
          >
            <Chev size={13} />
          </span>
        </button>
      </div>

      {/* Quick Action Button */}
      <button
        type="button"
        className="sidebar-cta"
        onClick={handleStartMeeting}
        title={_props.startLabel ? _props.startLabel : "Start a quick live meeting"}
      >
        <Spark size={15} />
        {!collapsed && <span>{_props.startLabel ?? "Quick Meeting"}</span>}
      </button>

      {/* Platform Navigation */}
      {!collapsed && <div className="sidebar-section-title">PLATFORM</div>}
      <nav className="sidebar-nav">
        <Link to="/dashboard" className="sidebar-link" title="Dashboard" activeProps={{ className: "sidebar-link active" }}>
          <span className="sidebar-link-icon"><Chart size={16} /></span>
          {!collapsed && <span>Dashboard</span>}
        </Link>
        <Link to="/tasks" search={{ client: "" }} className="sidebar-link" title="Tasks" activeProps={{ className: "sidebar-link active" }}>
          <span className="sidebar-link-icon"><Notebook size={16} /></span>
          {!collapsed && <span>Tasks</span>}
        </Link>
        <Link to="/clients" className="sidebar-link" title="Clients" activeProps={{ className: "sidebar-link active" }}>
          <span className="sidebar-link-icon"><Users size={16} /></span>
          {!collapsed && <span>Clients</span>}
        </Link>
        <Link to="/simulations" className="sidebar-link" title="Simulations" activeProps={{ className: "sidebar-link active" }}>
          <span className="sidebar-link-icon"><Brain size={16} /></span>
          {!collapsed && <span>Simulations</span>}
        </Link>
        <Link to="/training" className="sidebar-link" title="My Training" activeProps={{ className: "sidebar-link active" }}>
          <span className="sidebar-link-icon"><Doc size={16} /></span>
          {!collapsed && <span>My Training</span>}
        </Link>
        <Link to="/company" className="sidebar-link" title="Company Hub & Knowledge Base" activeProps={{ className: "sidebar-link active" }}>
          <span className="sidebar-link-icon"><Globe size={16} /></span>
          {!collapsed && <span>Company Hub</span>}
        </Link>
        <a href="/developers" className="sidebar-link" title="Developers & Integrations">
          <span className="sidebar-link-icon"><Bolt size={16} /></span>
          {!collapsed && <span>Developers</span>}
        </a>
        {(user?.interviewAccess || isAdminEmail(email)) && (
          <Link to="/interviews" className="sidebar-link" title="Interviews" activeProps={{ className: "sidebar-link active" }}>
            <span className="sidebar-link-icon"><User size={16} /></span>
            {!collapsed && <span>Interviews</span>}
          </Link>
        )}
      </nav>

      {/* Sidebar Footer Utilities & User Profile */}
      <div className="sidebar-bottom">
        <div className="sidebar-utils">
          <ThemeToggle />
          <NotificationBell />
          {bubbleHidden && user?.settings?.onboardingAgentEnabled !== false && (
            <button
              type="button"
              className="icon-btn xs"
              aria-label="Show SellSynq guide bubble"
              title="Show guide bubble"
              onClick={() => setBubbleHidden(false)}
            >
              <Chat size={16} />
            </button>
          )}
          <button
            type="button"
            className="icon-btn xs"
            aria-label="Help"
            title="Help & Guides"
            onClick={() => setHelpOpen(true)}
          >
            <Question size={16} />
          </button>
          {isAdminEmail(email) && (
            <Link to="/admin" className="icon-btn xs" aria-label="Admin" title="Admin">
              <Lock size={16} />
            </Link>
          )}
          <Link to="/settings" className="icon-btn xs" aria-label="Settings" title="Settings">
            <SettingsIcon size={16} />
          </Link>
        </div>

        {/* User Card */}
        <div className="sidebar-user" ref={ref} onClick={() => setOpen((o) => !o)}>
          <div className="sidebar-user-avatar" style={{ background: color }}>
            {initials}
          </div>
          <div className="sidebar-user-info">
            <div className="sidebar-user-name">{name}</div>
            <div className="sidebar-user-sub">{email}</div>
          </div>
          <button
            type="button"
            className="sidebar-logout-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleSignOut();
            }}
            title="Sign out"
          >
            <Logout size={14} />
          </button>

          {open && (
            <div className="user-pop sidebar-pop" onClick={(e) => e.stopPropagation()}>
              <div className="user-pop-head">
                <div className="who-avatar lg" style={{ background: color }}>
                  {initials}
                </div>
                <div>
                  <div className="user-pop-name">{name}</div>
                  <div className="user-pop-email mono">{email}</div>
                </div>
              </div>
              {user?.companyName && (
                <div style={{ padding: "8px 14px", borderBottom: "1px solid var(--border-soft)", fontSize: 12 }}>
                  <div style={{ color: "var(--text-3)", fontSize: 11 }}>Workspace</div>
                  <div style={{ fontWeight: 600, color: "var(--text-1)" }}>{user.companyName}</div>
                </div>
              )}
              <ul>
                <li
                  onClick={() => {
                    setOpen(false);
                    nav({ to: "/settings" });
                  }}
                >
                  <SettingsIcon size={14} /> Settings
                </li>
                <li
                  onClick={() => {
                    setOpen(false);
                    window.dispatchEvent(new Event(OPEN_ONBOARDING_AGENT_EVENT));
                  }}
                >
                  <Chat size={14} /> SellSynq guide &amp; coach
                </li>
              </ul>
              <button type="button" className="user-pop-out" onClick={handleSignOut}>
                <Logout size={12} /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>
      <OnboardingVideoModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </aside>
  );
}
